/**
 * LAREN CRAFT - Theme JavaScript
 * Handcrafted interactions for personalised products
 */

(function () {
    'use strict';

    // ==========================================================================
    // UTILITY FUNCTIONS
    // ==========================================================================

    const debounce = (fn, delay = 300) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => fn.apply(this, args), delay);
        };
    };

    const throttle = (fn, limit = 100) => {
        let inThrottle;
        return (...args) => {
            if (!inThrottle) {
                fn.apply(this, args);
                inThrottle = true;
                setTimeout(() => inThrottle = false, limit);
            }
        };
    };

    const formatMoney = (cents, format = window.theme?.moneyFormat) => {
        if (!format) format = '£{{amount}}';
        const price = (cents / 100).toFixed(2);
        return format.replace(/\{\{amount\}\}/g, price)
            .replace(/\{\{amount_no_decimals\}\}/g, Math.floor(cents / 100));
    };

    const fetchConfig = (type = 'json') => {
        return {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': `application/${type}`
            }
        };
    };

    // ==========================================================================
    // SCROLL ANIMATIONS
    // SEO-First: Content is visible by default. Animations only activate
    // after we confirm this is an interactive user session (not a crawler)
    // ==========================================================================

    class ScrollAnimations {
        constructor() {
            this.elements = document.querySelectorAll('.animate-fade-up, .animate-fade-in, .animate-fade-down, .animate-fade-left, .animate-fade-right, .animate-scale-up, .animate-stagger, [data-animate]');
            this.init();
        }

        init() {
            return;
            if (!window.theme?.settings?.enableAnimations) {
                return;
            }

            const isCrawler = this.detectCrawler();

            if (isCrawler) {
                // Don't enable animations for crawlers - content stays visible
                console.log('Crawler detected - animations disabled for SEO');
                return;
            }

            // Enable animations only for interactive users
            // This adds the class that triggers CSS to hide elements before animation
            requestAnimationFrame(() => {
                document.documentElement.classList.add('js-animations-ready');
            });

            // Fallback for browsers without IntersectionObserver
            if (!('IntersectionObserver' in window)) {
                this.elements.forEach(el => el.classList.add('is-visible'));
                return;
            }

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add('is-visible');
                        observer.unobserve(entry.target);
                    }
                });
            }, {
                threshold: 0.1,
                rootMargin: '0px 0px -50px 0px'
            });

            this.elements.forEach(el => observer.observe(el));
        }

        detectCrawler() {
            const ua = navigator.userAgent.toLowerCase();

            const crawlerPatterns = [
                'googlebot',
                'google-inspectiontool',
                'storebot-google',
                'googleother',
                'mediapartners-google',
                'feedfetcher-google',

                'bingbot',
                'yandex',
                'baiduspider',
                'duckduckbot',
                'slurp',
                'ia_archiver',

                'facebookexternalhit',
                'twitterbot',
                'linkedinbot',
                'pinterest',
                'slackbot',
                'discordbot',

                'applebot',
                'amazonbot',

                'lighthouse',
                'chrome-lighthouse',
                'pagespeed',
                'gtmetrix',
                'semrush',
                'ahrefs',
                'mj12bot',
                'dotbot',

                'headless',
                'phantomjs',
                'prerender'
            ];

            for (const pattern of crawlerPatterns) {
                if (ua.includes(pattern)) {
                    return true;
                }
            }
            if (navigator.webdriver === true) {
                return true;
            }

            return false;
        }
    }

    // ==========================================================================
    // HEADER
    // ==========================================================================

    class Header {
        constructor() {
            this.header = document.querySelector('[data-header]');
            if (!this.header) return;

            this.menuToggle = this.header.querySelector('[data-menu-toggle]');
            this.mobileNav = document.querySelector('[data-mobile-nav]');
            this.dropdownToggles = this.header.querySelectorAll('[data-dropdown-toggle]');
            this.searchToggle = this.header.querySelector('[data-search-toggle]');
            this.cartToggle = this.header.querySelector('[data-cart-toggle]');

            this.lastScrollY = 0;
            this.init();
        }

        init() {
            this.bindEvents();
            this.handleScroll();
        }

        bindEvents() {
            // Mobile menu toggle
            document.querySelectorAll('[data-menu-toggle]').forEach(toggle => {
                toggle.addEventListener('click', () => this.toggleMobileNav());
            });

            // Mobile dropdown toggles
            document.querySelectorAll('[data-mobile-dropdown-toggle]').forEach(toggle => {
                toggle.addEventListener('click', (e) => {
                    const submenu = toggle.nextElementSibling;
                    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
                    toggle.setAttribute('aria-expanded', !isExpanded);
                    submenu.hidden = isExpanded;
                });
            });

            // Desktop dropdown hovers
            this.dropdownToggles.forEach(toggle => {
                const parent = toggle.closest('.header__menu-item');
                parent.addEventListener('mouseenter', () => {
                    toggle.setAttribute('aria-expanded', 'true');
                });
                parent.addEventListener('mouseleave', () => {
                    toggle.setAttribute('aria-expanded', 'false');
                });
            });

            // Inline expanding search
            const searchInline = document.querySelector('[data-search-inline]');
            if (searchInline) {
                const openBtn = searchInline.querySelector('[data-search-open]');
                const closeBtn = searchInline.querySelector('[data-search-close]');
                const input = searchInline.querySelector('[data-search-input]');

                if (openBtn) {
                    openBtn.addEventListener('click', () => {
                        searchInline.classList.add('is-open');
                        setTimeout(() => input?.focus(), 100);
                    });
                }

                if (closeBtn) {
                    closeBtn.addEventListener('click', () => {
                        searchInline.classList.remove('is-open');
                        if (input) input.value = '';
                    });
                }

                // Close when clicking outside
                document.addEventListener('click', (e) => {
                    if (!searchInline.contains(e.target) && searchInline.classList.contains('is-open')) {
                        searchInline.classList.remove('is-open');
                    }
                });

                // Close on ESC key
                document.addEventListener('keydown', (e) => {
                    if (e.key === 'Escape' && searchInline.classList.contains('is-open')) {
                        searchInline.classList.remove('is-open');
                        if (input) input.value = '';
                    }
                });
            }

            // Cart toggle (for drawer)
            if (this.cartToggle && window.theme?.settings?.cartType === 'drawer') {
                this.cartToggle.addEventListener('click', (e) => {
                    e.preventDefault();
                    document.dispatchEvent(new CustomEvent('cart:open'));
                });
            }

            // Scroll handling
            window.addEventListener('scroll', throttle(() => this.handleScroll(), 100));

            // Escape key
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    this.closeMobileNav();
                }
            });
        }

        toggleMobileNav() {
            const isOpen = this.mobileNav?.classList.contains('is-open');
            if (isOpen) {
                this.closeMobileNav();
            } else {
                this.openMobileNav();
            }
        }

        openMobileNav() {
            this.mobileNav?.classList.add('is-open');
            this.menuToggle?.setAttribute('aria-expanded', 'true');
            document.body.style.overflow = 'hidden';
        }

        closeMobileNav() {
            this.mobileNav?.classList.remove('is-open');
            this.menuToggle?.setAttribute('aria-expanded', 'false');
            document.body.style.overflow = '';
        }

        handleScroll() {
            const scrollY = window.scrollY;

            if (scrollY > 50) {
                this.header.classList.add('is-scrolled');
            } else {
                this.header.classList.remove('is-scrolled');
            }

            // Hide/show on scroll direction
            if (scrollY > this.lastScrollY && scrollY > 200) {
                this.header.classList.add('is-hidden');
            } else {
                this.header.classList.remove('is-hidden');
            }

            this.lastScrollY = scrollY;
        }
    }

    // ==========================================================================
    // SLIDER COMPONENT (CSS Scroll-Snap based)
    // ==========================================================================

    class SliderComponent extends HTMLElement {
        constructor() {
            super();
            this.slider = null;
            this.slides = null;
            this.prevBtn = null;
            this.nextBtn = null;
            this.thumbnails = null;
            this.currentIndex = 0;
            this.scrollTimeout = null;
        }

        connectedCallback() {
            // Wait for DOM to be ready
            requestAnimationFrame(() => {
                this.slider = this.querySelector('[data-slider-container]');
                this.slides = this.querySelectorAll('[data-slide]');
                this.prevBtn = this.querySelector('[data-gallery-prev]');
                this.nextBtn = this.querySelector('[data-gallery-next]');
                this.thumbnails = this.querySelectorAll('[data-thumbnail]');

                this.bindEvents();
                this.initializeFromUrl();
            });
        }

        bindEvents() {
            // Navigation buttons
            this.prevBtn?.addEventListener('click', () => this.slide(-1));
            this.nextBtn?.addEventListener('click', () => this.slide(1));

            // Thumbnail clicks
            this.thumbnails?.forEach((thumb, i) => {
                thumb.addEventListener('click', () => this.goToSlide(i));
            });

            // Sync thumbnails on scroll
            this.slider?.addEventListener('scroll', () => {
                clearTimeout(this.scrollTimeout);
                this.scrollTimeout = setTimeout(() => this.updateThumbnails(), 50);
            }, { passive: true });
        }

        // Initialize gallery position based on URL variant parameter
        initializeFromUrl() {
            const urlParams = new URLSearchParams(window.location.search);
            const variantId = urlParams.get('variant');

            if (!variantId) return;

            // Find the variant data from the page
            const variantScript = document.querySelector('[data-variants]');
            if (!variantScript) return;

            try {
                const variants = JSON.parse(variantScript.textContent);
                const variant = variants.find(v => v.id == variantId);

                if (variant?.featured_media?.id) {
                    // Use setTimeout to ensure layout is complete
                    setTimeout(() => {
                        this.scrollToMediaId(variant.featured_media.id, false);
                    }, 100);
                }
            } catch (e) {
                console.error('Error parsing variants:', e);
            }
        }

        slide(direction) {
            const newIndex = this.currentIndex + direction;
            if (newIndex >= 0 && newIndex < this.slides.length) {
                this.goToSlide(newIndex);
            } else if (newIndex < 0) {
                this.goToSlide(this.slides.length - 1);
            } else {
                this.goToSlide(0);
            }
        }

        goToSlide(index, smooth = true) {
            const slide = this.slides[index];
            if (slide && this.slider) {
                // Calculate exact scroll position based on slide offset
                const scrollPosition = slide.offsetLeft;

                this.slider.scrollTo({
                    left: scrollPosition,
                    behavior: smooth ? 'smooth' : 'instant'
                });

                this.currentIndex = index;
                this.updateThumbnails();
            }
        }

        updateThumbnails() {
            if (!this.slider || !this.slides.length) return;
            const slideWidth = this.slides[0]?.offsetWidth || this.slider.offsetWidth;
            const index = Math.round(this.slider.scrollLeft / slideWidth);
            this.currentIndex = index;
            this.thumbnails?.forEach((t, i) => t.classList.toggle('is-active', i === index));
        }

        // Public method for variant image switching
        scrollToMediaId(mediaId, smooth = true) {
            // Convert mediaId to string for consistent comparison
            const mediaIdStr = String(mediaId);

            // Find slide by data-media-id AND data-slide attributes
            // This ensures we select the actual slide element, not thumbnail buttons
            const slide = this.querySelector(`[data-slide][data-media-id="${mediaIdStr}"]`);

            if (slide) {
                // Use data-slide attribute for reliable index lookup
                const index = parseInt(slide.dataset.slide, 10);
                if (!isNaN(index) && index >= 0) {
                    this.goToSlide(index, smooth);
                }
            }
        }
    }

    if (!customElements.get('slider-component')) {
        customElements.define('slider-component', SliderComponent);
    }

    // ==========================================================================
    // PRODUCT FORM - Minimal (main logic moved to web components)
    // ==========================================================================

    class ProductForm {
        constructor(container) {
            this.container = container;
            if (!this.container) return;
            this.bindQuantityButtons();
        }

        bindQuantityButtons() {
            // Only handle quantity buttons - variant and form handled by web components
            const quantityInput = this.container.querySelector('input[name="quantity"]');

            this.container.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
                const current = parseInt(quantityInput?.value || 1);
                if (current > 1 && quantityInput) quantityInput.value = current - 1;
            });

            this.container.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
                const current = parseInt(quantityInput?.value || 1);
                if (quantityInput) quantityInput.value = current + 1;
            });
        }
    }

    // ==========================================================================
    // CART
    // ==========================================================================

    class Cart {
        constructor() {
            this.cartCount = document.querySelector('[data-cart-count]');
            this.init();
        }

        init() {
            // Listen for cart updated events from web component
            document.addEventListener('cart:updated', () => this.updateCartCount());
        }

        async updateCartCount() {
            try {
                const response = await fetch('/cart.js');
                const cart = await response.json();

                // Update all cart count elements
                document.querySelectorAll('[data-cart-count]').forEach(el => {
                    el.textContent = cart.item_count;
                    el.classList.toggle('hidden', cart.item_count === 0);
                });
            } catch (error) {
                console.error('Error updating cart count:', error);
            }
        }
    }

    // ==========================================================================
    // FILTERS
    // ==========================================================================

    class Filters {
        constructor() {
            this.filtersSidebar = document.querySelector('[data-filters-sidebar]');
            this.filterToggles = document.querySelectorAll('[data-filter-toggle]');
            this.filterForm = document.querySelector('[data-filter-form]');
            this.sortSelect = document.querySelector('[data-sort-select]');

            this.init();
        }

        init() {
            if (!this.filtersSidebar) return;
            this.bindEvents();
        }

        bindEvents() {
            // Toggle sidebar
            this.filterToggles.forEach(toggle => {
                toggle.addEventListener('click', () => this.toggleSidebar());
            });

            // Sort change
            this.sortSelect?.addEventListener('change', (e) => {
                const url = new URL(window.location);
                url.searchParams.set('sort_by', e.target.value);
                window.location = url;
            });

            // Filter form submit
            this.filterForm?.addEventListener('submit', (e) => {
                e.preventDefault();
                const formData = new FormData(this.filterForm);
                const url = new URL(window.location.pathname, window.location.origin);

                for (const [key, value] of formData.entries()) {
                    if (value) url.searchParams.append(key, value);
                }

                window.location = url;
            });

            // Close on escape
            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') this.closeSidebar();
            });
        }

        toggleSidebar() {
            this.filtersSidebar?.classList.toggle('is-open');
            document.body.style.overflow = this.filtersSidebar?.classList.contains('is-open') ? 'hidden' : '';
        }

        closeSidebar() {
            this.filtersSidebar?.classList.remove('is-open');
            document.body.style.overflow = '';
        }
    }

    // ==========================================================================
    // MODALS
    // ==========================================================================

    class Modals {
        constructor() {
            this.init();
        }

        init() {
            document.querySelectorAll('[data-modal-close]').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    const modal = e.target.closest('.modal');
                    if (modal) this.close(modal);
                });
            });

            document.addEventListener('keydown', (e) => {
                if (e.key === 'Escape') {
                    document.querySelectorAll('.modal.is-open').forEach(modal => {
                        this.close(modal);
                    });
                }
            });
        }

        open(modal) {
            modal.classList.add('is-open');
            modal.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
        }

        close(modal) {
            modal.classList.remove('is-open');
            modal.setAttribute('aria-hidden', 'true');
            document.body.style.overflow = '';
        }
    }

    // ==========================================================================
    // ANNOUNCEMENT BAR
    // ==========================================================================

    class AnnouncementBar {
        constructor() {
            this.bar = document.querySelector('[data-announcement-bar]');
            this.closeBtn = this.bar?.querySelector('[data-announcement-close]');
            this.init();
        }

        init() {
            if (!this.bar) return;

            // Check if previously closed
            if (sessionStorage.getItem('announcement-closed')) {
                this.bar.classList.add('is-hidden');
            }

            this.closeBtn?.addEventListener('click', () => {
                this.bar.classList.add('is-hidden');
                sessionStorage.setItem('announcement-closed', 'true');
            });
        }
    }

    // ==========================================================================
    // PRODUCT LIGHTBOX (Native Dialog)
    // ==========================================================================


    class ProductLightbox {
        constructor() {
            this.lightbox = document.querySelector('[data-product-lightbox]');
            if (!this.lightbox) return;

            this.image = this.lightbox.querySelector('[data-lightbox-image]');
            this.thumbnails = this.lightbox.querySelectorAll('[data-lightbox-thumb]');
            this.prevBtn = this.lightbox.querySelector('[data-lightbox-prev]');
            this.nextBtn = this.lightbox.querySelector('[data-lightbox-next]');
            this.closeBtn = this.lightbox.querySelector('[data-lightbox-close]');

            // Build images array from thumbnails (no JSON parsing needed)
            this.images = Array.from(this.thumbnails).map(thumb => ({
                src: thumb.dataset.fullSrc,
                alt: thumb.querySelector('img')?.alt || ''
            }));
            this.currentIndex = 0;

            this.init();
        }

        init() {
            this.bindEvents();
        }

        bindEvents() {
            // Open lightbox on image click
            document.querySelectorAll('[data-zoom-wrapper]').forEach((wrapper, index) => {
                wrapper.addEventListener('click', () => this.open(index));
            });

            // Close button
            this.closeBtn?.addEventListener('click', () => this.close());

            // Click on backdrop (dialog element) to close
            this.lightbox.addEventListener('click', (e) => {
                if (e.target === this.lightbox) this.close();
            });

            // Navigation
            this.prevBtn?.addEventListener('click', () => this.prev());
            this.nextBtn?.addEventListener('click', () => this.next());

            // Thumbnail clicks
            this.thumbnails.forEach((thumb, index) => {
                thumb.addEventListener('click', () => this.goTo(index));
            });

            // Keyboard navigation (dialog has native ESC support, but we add arrow keys)
            this.lightbox.addEventListener('keydown', (e) => {
                if (e.key === 'ArrowLeft') this.prev();
                if (e.key === 'ArrowRight') this.next();
            });
        }

        open(index = 0) {
            this.currentIndex = index;
            this.updateImage();
            this.lightbox.showModal();
        }

        close() {
            this.lightbox.close();
        }

        updateImage() {
            if (!this.images[this.currentIndex]) return;

            const newSrc = this.images[this.currentIndex].src;
            const newAlt = this.images[this.currentIndex].alt;

            // Skip if same image
            if (this.image.src === newSrc) {
                this.updateThumbnailState();
                return;
            }

            // Show loading state
            const content = this.lightbox.querySelector('.product-lightbox__content');
            content?.classList.add('is-loading');
            this.image.style.opacity = '0';

            // Create new image to preload
            const tempImg = new Image();
            tempImg.onload = () => {
                this.image.src = newSrc;
                this.image.alt = newAlt;
                // Small delay for smooth transition
                setTimeout(() => {
                    content?.classList.remove('is-loading');
                    this.image.style.opacity = '1';
                }, 50);
            };
            tempImg.onerror = () => {
                // Still show the image even if preload fails
                this.image.src = newSrc;
                this.image.alt = newAlt;
                content?.classList.remove('is-loading');
                this.image.style.opacity = '1';
            };
            tempImg.src = newSrc;

            this.updateThumbnailState();
        }

        updateThumbnailState() {
            // Update thumbnails
            this.thumbnails.forEach((thumb, i) => {
                thumb.classList.toggle('is-active', i === this.currentIndex);
            });
        }

        prev() {
            this.currentIndex = this.currentIndex === 0 ? this.images.length - 1 : this.currentIndex - 1;
            this.updateImage();
        }

        next() {
            this.currentIndex = this.currentIndex === this.images.length - 1 ? 0 : this.currentIndex + 1;
            this.updateImage();
        }

        goTo(index) {
            this.currentIndex = index;
            this.updateImage();
        }
    }

    // ==========================================================================
    // PRODUCT REVIEWS
    // ==========================================================================

    class ProductReviews {
        constructor() {
            this.container = document.querySelector('.product-reviews');
            if (!this.container) return;

            this.formWrapper = this.container.querySelector('[data-review-form]');
            this.form = this.container.querySelector('[data-review-submit]');
            this.ratingStars = this.container.querySelectorAll('[data-rating]');
            this.ratingInput = this.container.querySelector('[data-rating-value]');
            this.openBtn = this.container.querySelector('[data-open-review-form]');
            this.closeBtn = this.container.querySelector('[data-close-review-form]');

            this.init();
        }

        init() {
            this.bindEvents();
        }

        bindEvents() {
            // Open/close form
            this.openBtn?.addEventListener('click', () => this.openForm());
            this.closeBtn?.addEventListener('click', () => this.closeForm());

            // Rating stars
            this.ratingStars.forEach(star => {
                star.addEventListener('click', () => {
                    const rating = parseInt(star.dataset.rating);
                    this.setRating(rating);
                });

                star.addEventListener('mouseenter', () => {
                    const rating = parseInt(star.dataset.rating);
                    this.highlightStars(rating);
                });
            });

            // Reset stars on mouse leave
            this.container.querySelector('[data-rating-input]')?.addEventListener('mouseleave', () => {
                const currentRating = parseInt(this.ratingInput?.value || 5);
                this.highlightStars(currentRating);
            });

            // Form submission
            this.form?.addEventListener('submit', (e) => this.handleSubmit(e));
        }

        openForm() {
            if (this.formWrapper) {
                this.formWrapper.hidden = false;
                this.formWrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }

        closeForm() {
            if (this.formWrapper) {
                this.formWrapper.hidden = true;
            }
        }

        setRating(rating) {
            if (this.ratingInput) {
                this.ratingInput.value = rating;
            }
            this.highlightStars(rating);
        }

        highlightStars(rating) {
            this.ratingStars.forEach(star => {
                const starRating = parseInt(star.dataset.rating);
                star.classList.toggle('is-active', starRating <= rating);
            });
        }

        async handleSubmit(e) {
            e.preventDefault();

            const formData = new FormData(this.form);
            const reviewData = {
                rating: formData.get('rating'),
                name: formData.get('name'),
                email: formData.get('email'),
                title: formData.get('title'),
                content: formData.get('content'),
                product_id: this.getProductId(),
                created_at: new Date().toISOString()
            };

            // For now, just show a success message
            // In production, this would send to a review service or Shopify metafields
            console.log('Review submitted:', reviewData);

            this.showNotification('Thank you! Your review has been submitted for approval.');
            this.closeForm();
            this.form.reset();
            this.setRating(5);
        }

        getProductId() {
            const productJson = document.querySelector('[data-product-json]');
            if (productJson) {
                try {
                    const product = JSON.parse(productJson.textContent);
                    return product.id;
                } catch (e) {
                    return null;
                }
            }
            return null;
        }

        showNotification(message) {
            const toast = document.getElementById('notification-toast');
            if (toast) {
                const msgEl = toast.querySelector('.toast__message');
                if (msgEl) msgEl.textContent = message;
                toast.classList.add('is-visible', 'toast--success');
                toast.setAttribute('aria-hidden', 'false');
                setTimeout(() => {
                    toast.classList.remove('is-visible', 'toast--success');
                    toast.setAttribute('aria-hidden', 'true');
                }, 4000);
            }
        }
    }

    // ==========================================================================
    // VARIANT IMAGE SWITCHER - Switches gallery to variant's featured image
    // ==========================================================================

    class VariantImageSwitcher {
        constructor() {
            this.productJson = document.querySelector('[data-product-json]');
            this.gallery = document.querySelector('[data-product-gallery]');

            if (!this.productJson || !this.gallery) return;

            this.product = this.parseProduct();
            this.mediaList = this.gallery.querySelectorAll('[data-slide]');
            this.thumbnails = this.gallery.querySelectorAll('[data-thumbnail]');

            this.init();
        }

        parseProduct() {
            try {
                return JSON.parse(this.productJson.textContent);
            } catch (e) {
                console.error('Error parsing product JSON:', e);
                return null;
            }
        }

        init() {
            if (!this.product) return;
            this.bindEvents();
        }

        bindEvents() {
            // Listen for variant changes from radio/select inputs
            document.querySelectorAll('.product-info__swatch input[type="radio"], select[name*="options"]').forEach(input => {
                input.addEventListener('change', () => {
                    setTimeout(() => this.handleVariantChange(), 50);
                });
            });

            // Listen for custom variant:changed event
            document.addEventListener('variant:changed', (e) => {
                const variant = e.detail?.variant;
                if (variant) {
                    this.switchToVariantImage(variant);
                }
            });
        }

        handleVariantChange() {
            // Find the currently selected variant
            const selectedOptions = [];
            document.querySelectorAll('.product-info__option').forEach(optionGroup => {
                const checked = optionGroup.querySelector('input[type="radio"]:checked');
                if (checked) {
                    selectedOptions.push(checked.value);
                }
            });

            // Find matching variant
            const variant = this.product.variants.find(v => {
                return selectedOptions.every((opt, index) => v[`option${index + 1}`] === opt);
            });

            if (variant) {
                this.switchToVariantImage(variant);
            }
        }

        switchToVariantImage(variant) {
            if (!variant.featured_media) return;

            const mediaId = variant.featured_media.id;

            // Use SliderComponent's native method if available
            const sliderComponent = document.querySelector('slider-component');
            if (sliderComponent && sliderComponent.scrollToMediaId) {
                sliderComponent.scrollToMediaId(mediaId);
                return;
            }

            // Fallback: scroll using scrollIntoView
            const targetSlide = this.gallery?.querySelector(`[data-media-id="${mediaId}"]`);
            if (targetSlide) {
                targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });

                // Update thumbnails
                const index = Array.from(this.mediaList).indexOf(targetSlide);
                this.thumbnails.forEach((thumb, i) => {
                    thumb.classList.toggle('is-active', i === index);
                });
            }
        }
    }

    // ==========================================================================
    // INIT
    // ==========================================================================

    const init = () => {
        new ScrollAnimations();
        new Header();
        new Cart();
        new Modals();
        new AnnouncementBar();
        new Filters();

        // Product page
        const productSection = document.querySelector('[data-section-type="hero"]') || document.querySelector('.product-section');
        if (productSection) {
            // SliderComponent is a web component, auto-initializes
            new ProductForm(productSection);
            new ProductLightbox();
            new ProductReviews();
            new VariantImageSwitcher();
        }



        // Lazy load images
        if ('loading' in HTMLImageElement.prototype) {
            document.querySelectorAll('img[loading="lazy"]').forEach(img => {
                img.src = img.src;
            });
        }
    };

    // Run on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Shopify theme editor support
    document.addEventListener('shopify:section:load', () => {
        init();
    });

})();
